import React, { useEffect, useMemo, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FiX } from "react-icons/fi";
import { motion, AnimatePresence } from "framer-motion";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";

import { db } from "../../firebase/config";
import { useAuth } from "../../context/AuthContext";
import { getTodayId } from "../../utils/date";
import {
  vaginalBleedingColourOptions,
  vaginalBleedingFlowOptions,
  clotOptions,
} from "../../config/symptomsConfig";

import {
  FlowSectionCard,
  ColourSectionCard,
  ClotsSectionCard,
} from "../../components/recovery/bleeding";

type RiskLevel = "normal" | "monitor" | "alert";

const RISK_WEIGHT: Record<RiskLevel, number> = { normal: 0, monitor: 1, alert: 2 };

const maxRisk = (a?: RiskLevel, b?: RiskLevel): RiskLevel => {
  const ra = a ?? "normal";
  const rb = b ?? "normal";
  return RISK_WEIGHT[ra] >= RISK_WEIGHT[rb] ? ra : rb;
};

type VaginalBleedingState = {
  colour: string | null;
  flow: string | null;
  clots: string | null;
};

export default function BleedingCheckin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const selectedWeek: number =
    (location.state as { week?: number })?.week ?? 1;

  const todayId = useMemo(() => {
    const dateFromState = (location.state as { dateId?: string; week?: number })?.dateId;
    return dateFromState ?? getTodayId();
  }, [location.state]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [vb, setVb] = useState<VaginalBleedingState>({
    colour: null,
    flow: null,
    clots: null,
  });

  const noBleedingValue = useMemo(() => {
    const opt =
      vaginalBleedingFlowOptions.find((o) => String(o.label).toLowerCase().includes("no")) ??
      vaginalBleedingFlowOptions.find((o) => String(o.value).toLowerCase().includes("no")) ??
      vaginalBleedingFlowOptions.find((o) => String(o.value).toLowerCase().includes("none"));
    return opt?.value;
  }, []);

  const isNoBleeding = vb.flow != null && vb.flow === noBleedingValue;

  useEffect(() => {
    const load = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const ref = doc(db, "users", user.uid, "recoveryDailyLogs", todayId);
        const snap = await getDoc(ref);
        if (snap.exists()) {
          const data = snap.data() as {
            symptoms?: {
              vaginalBleeding?: {
                colour?: string;
                flow?: string;
                clots?: string;
              };
            };
          };
          const existing = data?.symptoms?.vaginalBleeding ?? {};
          setVb({
            colour: existing.colour ?? null,
            flow: existing.flow ?? null,
            clots: existing.clots ?? null,
          });
        }
      } catch {
        setError("Could not load your bleeding log.");
      } finally {
        setLoading(false);
      }
    };
    const raf = requestAnimationFrame(() => {
      load();
    });
    return () => {
      cancelAnimationFrame(raf);
      if (closeTimer.current) clearTimeout(closeTimer.current);
    };
  }, [user, todayId]);

  const fromDashboard = (location.state as { from?: string })?.from === "dashboard";

  const handleClose = () => {
    if (fromDashboard) {
      navigate("/dashboard");
    } else {
      navigate("/recovery", { state: { week: selectedWeek } });
    }
  };

  const setFlow = (value: string) => {
    if (value === noBleedingValue) setVb((prev) => ({ ...prev, flow: value, colour: null, clots: null }));
    else setVb((prev) => ({ ...prev, flow: value }));
  };

  const setColour = (value: string) => setVb((prev) => ({ ...prev, colour: value }));
  const setClots = (value: string) => setVb((prev) => ({ ...prev, clots: value }));

  const canSubmit = Boolean(vb.flow) && (isNoBleeding || Boolean(vb.colour));

  const handleSave = async () => {
    if (!user || !canSubmit) return;

    setSaving(true);
    setError(null);
    try {
      const colourOpt = vaginalBleedingColourOptions.find(
        (o) => o.value === vb.colour,
      );
      const flowOpt = vaginalBleedingFlowOptions.find(
        (o) => o.value === vb.flow,
      );
      const clotOpt = clotOptions.find((o) => o.value === vb.clots);

      const colourRisk: RiskLevel = (colourOpt?.risk ?? "normal") as RiskLevel;
      const flowRisk: RiskLevel = (flowOpt?.risk ?? "normal") as RiskLevel;
      const clotRisk: RiskLevel = (clotOpt?.risk ?? "normal") as RiskLevel;

      let overallRisk: RiskLevel = "normal";
      overallRisk = maxRisk(overallRisk, colourRisk);
      overallRisk = maxRisk(overallRisk, flowRisk);
      overallRisk = maxRisk(overallRisk, clotRisk);

      const ref = doc(db, "users", user.uid, "recoveryDailyLogs", todayId);

      await setDoc(
        ref,
        {
          userId: user.uid,
          dateId: todayId,
          date: serverTimestamp(),
          programmeId: "physical_wellbeing_6w",
          weekNumber: selectedWeek,
          symptoms: {
            vaginalBleeding: {
              colour: vb.colour ?? null,
              flow: vb.flow ?? null,
              clots: vb.clots ?? null,
              colourRisk: vb.colour ? colourRisk : "normal",
              clotRisk: vb.clots ? clotRisk : "normal",
              overallRisk,
            },
          },
          updatedAt: serverTimestamp(),
          createdAt: serverTimestamp(),
        },
        { merge: true },
      );

      setSaved(true);
      closeTimer.current = setTimeout(handleClose, 700);
    } catch {
      setError("Could not save. Please try again.");
      setSaving(false);
    }
  };

  // ── HIGH QUALITY BRANDED SPINNER (Initial Load) ───────────────────────────
  if (loading) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100%',
        minHeight: '-webkit-fill-available', 
        backgroundColor: '#fcf9f7',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2147483647,
        pointerEvents: 'all'
      }}>
        {/* Spinner */}
        <div style={{
          width: '50px',
          height: '50px',
          border: '4px solid rgba(30, 94, 91, 0.1)',
          borderTop: '4px solid #1e5e5b',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
          marginBottom: '20px',
          boxSizing: 'border-box'
        }} />

        {/* Branding */}
        <div style={{
          fontFamily: 'DM Sans, sans-serif',
          textAlign: 'center'
        }}>
          <p style={{
            color: '#0c2624',
            fontSize: '18px',
            fontWeight: '600',
            letterSpacing: '2px',
            textTransform: 'uppercase',
            margin: 0,
            animation: 'pulse 1.5s ease-in-out infinite'
          }}>
            BodyOwn
          </p>
        </div>

        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
          body { overflow: hidden !important; }
        `}</style>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[200]">
      <motion.button
        type="button"
        aria-label="Close"
        onClick={handleClose}
        className="absolute inset-0 bg-black/30"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.2 }}
      />

      <motion.div
        className="absolute bottom-0 left-0 right-0 mx-auto w-full max-w-[430px]"
        initial={{ y: "100%" }}
        animate={{ y: 0 }}
        transition={{ type: "spring", stiffness: 340, damping: 32, mass: 0.9 }}
      >
        <article className="relative bg-[#fcf9f7] rounded-t-[32px] shadow-2xl max-h-[85vh] overflow-y-auto">
          <div className="px-6 pt-6 pb-4 relative">
            <div className="mx-auto mb-4 h-1.5 w-12 rounded-full bg-black/10" />

            <button
              type="button"
              onClick={handleClose}
              aria-label="Close"
              className="absolute top-5 right-5 border-none bg-white/70 backdrop-blur inline-flex items-center justify-center w-9 h-9 rounded-full hover:bg-white transition-colors"
            >
              <FiX size={20} />
            </button>

            <h2 className="m-0 font-semibold text-2xl text-black leading-normal">
              Are you having any vaginal bleeding today?
            </h2>
          </div>

          <div className="px-6 pb-28 flex flex-col gap-6">
            <FlowSectionCard
              value={vb.flow}
              onChange={setFlow}
              isNoBleeding={isNoBleeding}
              disabled={loading || !user || saving || saved}
            />

            {!isNoBleeding && (
              <ColourSectionCard
                value={vb.colour}
                onChange={setColour}
                disabled={loading || !user || saving || saved}
              />
            )}

            {!isNoBleeding && (
              <ClotsSectionCard
                value={vb.clots}
                onChange={setClots}
                disabled={loading || !user || saving || saved}
              />
            )}

            {error && (
              <p className="text-sm font-medium text-red-600">{error}</p>
            )}
          </div>

          <div className="sticky bottom-0 bg-[#fcf9f7] border-t border-black/5 px-6 py-4">
            <button
              type="button"
              onClick={handleSave}
              disabled={!user || loading || saving || !canSubmit || saved}
              className={[
                "btn-submit transition-all duration-200",
                saved ? "!bg-green-600" : "",
              ].join(" ")}
            >
              <AnimatePresence mode="wait" initial={false}>
                {saved ? (
                  <motion.span
                    key="saved"
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="flex items-center justify-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                    Saved!
                  </motion.span>
                ) : saving ? (
                  <motion.span
                    key="saving"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="flex items-center justify-center gap-2"
                  >
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z" />
                    </svg>
                    Saving…
                  </motion.span>
                ) : (
                  <motion.span key="idle" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                    Submit
                  </motion.span>
                )}
              </AnimatePresence>
            </button>
          </div>
        </article>
      </motion.div>
    </div>
  );
}